/**
 * Radar — Modal d'impact AI (score + narratif défendable comité + listes + actions).
 */
import {
  X, Target, ExternalLink, Loader2, TrendingUp, TrendingDown, Activity,
  Clock, Crown, ArrowRight, Building2, AlertTriangle, GitMerge, Coins, Zap,
  RefreshCw, FileDown,
} from "lucide-react";

export function ImpactModal({ signal, data, loading, onClose, onRecompute, onExportPdf }) {
  const score = data?.score_impact;
  const direction = data?.direction || "neutre";
  const horizon = data?.horizon || "—";
  const scoreColor = score >= 7 ? "#EF4444" : score >= 4 ? "#EF9F27" : "#1D9E75";
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="radar-impact-modal"
    >
      <div
        className="noam-card w-full max-w-3xl my-8 p-6 space-y-4 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-zinc-500 hover:text-zinc-200"
          data-testid="radar-impact-modal-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="space-y-1">
          <div className="flex items-center gap-2 noam-overline text-[#378ADD]">
            <Target className="w-3 h-3" /> Impact stratégique
          </div>
          <h2 className="noam-display text-lg font-bold leading-tight pr-8">{signal.title}</h2>
          <a
            href={signal.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] text-zinc-500 hover:text-[#378ADD] inline-flex items-center gap-1"
          >
            <ExternalLink className="w-2.5 h-2.5" /> Source
          </a>
        </div>

        {loading ? (
          <div className="flex flex-col items-center gap-2 py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[#378ADD]" />
            <div className="text-xs text-zinc-400">Noam analyse l'impact sur ton workspace…</div>
          </div>
        ) : !data ? (
          <div className="text-xs text-zinc-500 py-12 text-center">Aucune donnée.</div>
        ) : (
          <>
            {/* Verdict header */}
            <div className="grid grid-cols-3 gap-3">
              <div className="border rounded-md p-3 text-center" style={{ borderColor: `${scoreColor}50`, background: `${scoreColor}10` }}>
                <div className="text-[9px] uppercase tracking-wider text-zinc-500">Score impact</div>
                <div className="text-3xl font-bold mt-1" style={{ color: scoreColor }}>{score ?? "—"}<span className="text-sm text-zinc-500">/10</span></div>
              </div>
              <div className="border border-white/10 rounded-md p-3 text-center">
                <div className="text-[9px] uppercase tracking-wider text-zinc-500">Direction</div>
                <div className={`text-base font-bold mt-2 inline-flex items-center gap-1 ${
                  direction === "menace" ? "text-red-400" :
                  direction === "opportunite" ? "text-emerald-400" :
                  direction === "mixte" ? "text-[#EF9F27]" : "text-zinc-400"
                }`}>
                  {direction === "menace" && <TrendingDown className="w-4 h-4" />}
                  {direction === "opportunite" && <TrendingUp className="w-4 h-4" />}
                  {direction === "mixte" && <Activity className="w-4 h-4" />}
                  {direction}
                </div>
              </div>
              <div className="border border-white/10 rounded-md p-3 text-center">
                <div className="text-[9px] uppercase tracking-wider text-zinc-500">Horizon</div>
                <div className="text-base font-bold mt-2 text-zinc-200 inline-flex items-center gap-1">
                  <Clock className="w-4 h-4 text-zinc-500" />
                  {horizon.replace("_", " ")}
                </div>
              </div>
            </div>

            {/* Synthèse + Narratif */}
            {data.synthese && (
              <div className="border border-white/10 rounded-md p-3 bg-white/[0.02]">
                <div className="noam-overline text-[#378ADD] mb-1">Synthèse</div>
                <p className="text-sm text-zinc-200 leading-relaxed">{data.synthese}</p>
              </div>
            )}
            {data.narratif && (
              <div className="border border-[#EF9F27]/30 rounded-md p-3 bg-[#EF9F27]/5">
                <div className="noam-overline text-[#EF9F27] mb-1 flex items-center gap-1">
                  <Crown className="w-3 h-3" /> Narratif défendable en comité
                </div>
                <p className="text-sm text-zinc-100 leading-relaxed italic">"{data.narratif}"</p>
              </div>
            )}
            {data.mecanisme_transmission && (
              <div className="text-[11px] text-zinc-400 flex items-start gap-2">
                <ArrowRight className="w-3 h-3 mt-0.5 text-zinc-500 flex-shrink-0" />
                <div><strong className="text-zinc-300">Mécanisme de transmission :</strong> {data.mecanisme_transmission}</div>
              </div>
            )}

            {/* Lists */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ImpactList title="Entités impactées" icon={Building2} color="#378ADD" items={data.entites_impactees} renderName={(x) => x.name} testId="radar-impact-entites" />
              <ImpactList title="Risques activés" icon={AlertTriangle} color="#EF4444" items={data.risques_impactes} renderName={(x) => x.title} testId="radar-impact-risques" />
              <ImpactList title="Négociations touchées" icon={GitMerge} color="#EF9F27" items={data.negociations_impactees} renderName={(x) => x.name} testId="radar-impact-negos" />
              <ImpactList title="Titres boursiers" icon={Coins} color="#1D9E75" items={data.tickers_impactes} renderName={(x) => `${x.ticker} — ${x.name || ""}`} testId="radar-impact-tickers" />
            </div>

            {/* Actions */}
            {data.actions_recommandees?.length > 0 && (
              <div className="border border-[#1D9E75]/30 rounded-md p-3 bg-[#1D9E75]/5">
                <div className="noam-overline text-[#1D9E75] mb-2 flex items-center gap-1">
                  <Zap className="w-3 h-3" /> Actions recommandées
                </div>
                <ol className="space-y-1.5">
                  {data.actions_recommandees.map((a, i) => (
                    <li key={i} className="text-xs text-zinc-200 flex items-start gap-2">
                      <span className="w-4 h-4 rounded-full bg-[#1D9E75]/20 text-[#1D9E75] text-[9px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                      <span>{a}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
              <button type="button" onClick={onExportPdf} className="btn-ghost text-xs" data-testid="radar-impact-export-pdf" title="Génère un brief comité PDF prêt à distribuer">
                <FileDown className="w-3 h-3" /> Exporter en note PDF
              </button>
              <button type="button" onClick={onRecompute} className="btn-ghost text-xs" data-testid="radar-impact-recompute">
                <RefreshCw className="w-3 h-3" /> Recalculer
              </button>
              <button type="button" onClick={onClose} className="btn-primary text-xs" data-testid="radar-impact-close-btn">
                Fermer
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ImpactList({ title, icon: Icon, color, items, renderName, testId }) {
  const list = items || [];
  return (
    <div className="border rounded-md p-3" style={{ borderColor: `${color}30`, background: `${color}05` }} data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold" style={{ color }}>
          <Icon className="w-3 h-3" /> {title}
        </div>
        <span className="text-[10px] text-zinc-500">×{list.length}</span>
      </div>
      {list.length === 0 ? (
        <div className="text-[10px] text-zinc-600 italic">Rien d'impacté.</div>
      ) : (
        <ul className="space-y-1.5">
          {list.map((x, i) => (
            <li key={x.id || i} className="text-[11px]">
              <div className="text-zinc-200 font-medium">{renderName(x)}</div>
              {x.raison && <div className="text-zinc-500 text-[10px] mt-0.5 leading-snug">{x.raison}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
